"use client";

import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";

// Hardcoded Our Inspiration slider data
const ourInspirationSliders = [
  {
    id: 1,
    title: "Late Padma Bhushan Shri Ramvilas Paswan ji",
    excerpt: "Late Padma Bhushan Shri Ramvilas Paswan ji",
    imageUrl: "/images/Our-Inspiration-Shri-Ramvilas-Paswan.webp",
    mobileImageUrl: "/images/Our-Inspiration-Shri-Ramvilas-Paswan-mobile.webp",
    buttonText: "Read More",
    buttonLink: "/our-inspiration",
    linkTarget: "_self",
  },
];

export default function OurInspirationSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % ourInspirationSliders.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % ourInspirationSliders.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) =>
        (prev - 1 + ourInspirationSliders.length) % ourInspirationSliders.length
    );
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const currentSlider = ourInspirationSliders[currentSlide];

  return (
    <section className="relative h-screen overflow-hidden bg-gradient-to-r from-green-900 to-green-800">
      {/* Background Images */}
      <div className="absolute inset-0">
        {ourInspirationSliders.map((slider, index) => (
          <div
            key={slider.id}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 ease-in-out ${
              index === currentSlide
                ? "opacity-100 scale-100"
                : "opacity-0 scale-105"
            }`}
            style={{
              backgroundImage: `url(${
                isMobile && slider.mobileImageUrl
                  ? slider.mobileImageUrl
                  : slider.imageUrl
              })`,
            }}
          />
        ))}
      </div>

      {/* Removed full-screen gradient overlay. Vignette will be applied only under content. */}

      {/* Content Overlay - Bottom and Center with local vignette */}
      <div className="absolute inset-0 flex flex-col justify-end z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-8 relative">
          {/* Vignette effect only behind the content area (bottom-to-top) */}
          <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-40 sm:h-56 lg:h-64 bg-gradient-to-t from-black/70 via-black/40 to-transparent"></div>
          <div className="relative max-w-4xl mx-auto text-center">
            {/* Section Title */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight drop-shadow-2xl">
              Our Inspiration
            </h2>

            {/* Slider Content */}
            {currentSlider.excerpt && (
              <p className="text-lg sm:text-xl lg:text-2xl text-white/90 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
                {currentSlider.excerpt}
              </p>
            )}
            {currentSlider.buttonText && currentSlider.buttonLink && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-transparent hover:bg-white/20 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 border-2 border-white/50 hover:border-white"
                  onClick={() => {
                    if (currentSlider.linkTarget === "_blank") {
                      window.open(currentSlider.buttonLink, "_blank");
                    } else {
                      window.location.href = currentSlider.buttonLink;
                    }
                  }}
                >
                  {currentSlider.buttonText}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Arrows - Only show if more than 1 slide */}
      {ourInspirationSliders.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Play/Pause Button - Only show if more than 1 slide */}
      {ourInspirationSliders.length > 1 && (
        <button
          onClick={togglePlayPause}
          className="absolute bottom-8 right-8 z-20 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
          aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </button>
      )}
    </section>
  );
}
